import { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

type AuthContextValue = {
  userId: string | null;
  email: string | null;
  isAdmin: boolean;
  loading: boolean;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [userId, setUserId] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const checkRole = async (id: string) => {
    // Try to claim first admin (no-op if any admin exists)
    try { await supabase.rpc("claim_first_admin" as never); } catch { /* ignore */ }
    const { data } = await supabase.rpc("has_role", { _user_id: id, _role: "admin" });
    setIsAdmin(Boolean(data));
  };

  useEffect(() => {
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      const id = session?.user.id ?? null;
      setUserId(id);
      setEmail(session?.user.email ?? null);
      setIsAdmin(false);
      if (id) setTimeout(() => void checkRole(id), 0);
    });

    supabase.auth.getSession().then(async ({ data }) => {
      const id = data.session?.user.id ?? null;
      setUserId(id);
      setEmail(data.session?.user.email ?? null);
      if (id) await checkRole(id);
      setLoading(false);
    });

    return () => listener.subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    setUserId(null);
    setEmail(null);
    setIsAdmin(false);
  };

  return (
    <AuthContext.Provider value={{ userId, email, isAdmin, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};


export const RequireAdmin = ({ children }: { children: ReactNode }) => {
  const { userId, isAdmin, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen grid place-items-center text-muted-foreground">…</div>;
  if (!userId) return <Navigate to="/auth" state={{ from: location.pathname }} replace />;
  if (!isAdmin) {
    return (
      <section className="min-h-screen bg-background px-4 py-16">
        <div className="mx-auto max-w-2xl rounded-xl border border-destructive/40 bg-destructive/10 p-10 text-center">
          <h1 className="text-3xl font-black text-destructive">Access Denied</h1>
          <p className="mt-3 font-bold text-muted-foreground">You don't have permission to view this page.</p>
        </div>
      </section>
    );
  }
  return <>{children}</>;
};
